import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, ChevronDown, Loader2, MessageSquareText, SendHorizontal, Sparkles, WandSparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { ImprovedVersionCard } from "@/components/results/ImprovedVersionCard";
import { PromptScoreSummaryBar } from "@/components/results/PromptScoreSummaryBar";
import { supabase } from "@/integrations/supabase/client";
import { TOOL_COLORS, TOOLS_BY_CATEGORY } from "@/lib/toolConstants";
import { cn } from "@/lib/utils";
import type { ToolRecommendation } from "@/components/prompt-builder/ToolRecommendationCards";
import type { PromptResponseIteration, ResponseDirection } from "@/types/promptResponse";

interface PromptResponsePanelProps {
  scoreId?: string;
  originalPrompt: string;
  promptSummary?: string;
  toolRecommendations?: ToolRecommendation[] | null;
  recommendationId?: string | null;
  selectedTool?: string | null;
  iterations: PromptResponseIteration[];
  onIterationsChange: (iterations: PromptResponseIteration[]) => void;
  onRemixPrompt?: (prompt: string) => void;
}

type DimensionScoreValue = number | { score: number; rationale?: string };
type FollowUpOptimizedPrompts = Record<string, { prompt: string; model?: string; updated_at?: string }>;

const RANK_LABELS = ["Best", "Better", "Good"];

function normalizeIteration(raw: PromptResponseIteration): PromptResponseIteration {
  return {
    ...raw,
    directions_json: Array.isArray(raw.directions_json) ? raw.directions_json : [],
    specificity_examples_json: Array.isArray(raw.specificity_examples_json) ? raw.specificity_examples_json : [],
    analysis_json: raw.analysis_json && typeof raw.analysis_json === "object" ? raw.analysis_json : {},
  };
}

function getAnalysisString(iteration: PromptResponseIteration, key: string) {
  const value = iteration.analysis_json?.[key];
  return typeof value === "string" ? value : "";
}

function getAnalysisNumber(iteration: PromptResponseIteration, key: string) {
  const value = iteration.analysis_json?.[key];
  return typeof value === "number" ? value : null;
}

function getAnalysisRecord(iteration: PromptResponseIteration, key: string) {
  const value = iteration.analysis_json?.[key];
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function getToolModel(toolName: string | null | undefined, recommendations?: ToolRecommendation[] | null) {
  if (!toolName) return "";
  return recommendations?.find((rec) => rec.tool === toolName)?.model || "";
}

function getFollowUpOptimizedPrompts(iteration: PromptResponseIteration): FollowUpOptimizedPrompts {
  const cached = getAnalysisRecord(iteration, "follow_up_optimized_prompts");
  if (!cached) return {};

  return Object.entries(cached).reduce<FollowUpOptimizedPrompts>((acc, [toolName, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;
      if (typeof record.prompt === "string") {
        acc[toolName] = {
          prompt: record.prompt,
          model: typeof record.model === "string" ? record.model : undefined,
          updated_at: typeof record.updated_at === "string" ? record.updated_at : undefined,
        };
      }
    }
    return acc;
  }, {});
}

function formatStateLabel(value: string) {
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getConversationStateText(iteration: PromptResponseIteration) {
  return (
    getAnalysisString(iteration, "on_track_assessment") ||
    iteration.decision_translation ||
    iteration.response_summary ||
    "Cosmo analyzed the response and found the next useful move."
  );
}

function SuggestionAction({
  direction,
  onSelect,
}: {
  direction: ResponseDirection;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="group flex h-full min-h-[112px] w-full flex-col justify-between rounded-lg border border-border/70 bg-background/70 p-3 text-left transition-colors hover:border-primary/45 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="flex items-start gap-2">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <WandSparkles className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-foreground">{direction.direction}</span>
          <span className="mt-1 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
            {direction.where_that_leads}
          </span>
        </span>
      </span>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
        Add to instruction
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function FollowUpModelPicker({
  recommendations,
  selectedTool,
  selectedModel,
  isOptimizing,
  disabled,
  onSelect,
}: {
  recommendations?: ToolRecommendation[] | null;
  selectedTool: string | null;
  selectedModel: string;
  isOptimizing: boolean;
  disabled: boolean;
  onSelect: (toolName: string, toolModel: string) => void;
}) {
  const rankedToolNames = useMemo(
    () => new Set((recommendations || []).map((rec) => rec.tool)),
    [recommendations]
  );
  const displayTool = selectedTool || recommendations?.[0]?.tool || "Select model";
  const displayLabel = selectedModel || getToolModel(displayTool, recommendations) || displayTool;

  return (
    <div className="flex items-center gap-3">
      <span className="hidden text-sm font-semibold text-muted-foreground md:inline">
        Optimized for:
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="h-10 min-w-[136px] max-w-[220px] justify-between rounded-xl border-2 border-success px-4 text-foreground hover:bg-success/10"
          >
            <span className="truncate">
              {isOptimizing ? "Optimizing..." : displayLabel}
            </span>
            {isOptimizing ? (
              <Loader2 className="ml-3 h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <ChevronDown className="ml-3 h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[420px] w-[320px] overflow-y-auto">
          {recommendations && recommendations.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                Recommended
              </DropdownMenuLabel>
              {recommendations.map((rec, index) => (
                <DropdownMenuItem
                  key={`${rec.rank}-${rec.tool}`}
                  className={cn("items-start gap-3 py-2", TOOL_COLORS[rec.tool])}
                  onSelect={() => onSelect(rec.tool, rec.model || "")}
                >
                  <span className="mt-0.5 rounded-full bg-primary/20 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    {RANK_LABELS[index] || `#${rec.rank}`}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      {rec.tool}
                      {selectedTool === rec.tool && <Check className="h-3.5 w-3.5 text-success" />}
                    </span>
                    {rec.model && (
                      <span className="block text-xs text-muted-foreground">{rec.model}</span>
                    )}
                  </span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
            All tools
          </DropdownMenuLabel>
          {Object.entries(TOOLS_BY_CATEGORY).map(([category, tools]) => {
            const availableTools = tools.filter((tool) => !rankedToolNames.has(tool));
            if (availableTools.length === 0) return null;

            return (
              <div key={category}>
                <DropdownMenuLabel className="px-2 py-1 text-[11px] font-medium text-muted-foreground">
                  {category}
                </DropdownMenuLabel>
                {availableTools.map((tool) => (
                  <DropdownMenuItem
                    key={tool}
                    className={cn("justify-between", TOOL_COLORS[tool])}
                    onSelect={() => onSelect(tool, getToolModel(tool, recommendations))}
                  >
                    <span>{tool}</span>
                    {selectedTool === tool && <Check className="h-3.5 w-3.5 text-success" />}
                  </DropdownMenuItem>
                ))}
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {isOptimizing && <Sparkles className="hidden h-4 w-4 text-primary md:block" />}
    </div>
  );
}

const isMockMode = import.meta.env.VITE_RESPONSE_STEP_MOCK === "true";

function buildMockAnalysis(args: {
  scoreId?: string;
  originalPrompt: string;
  responseText: string;
  turnIndex: number;
}): PromptResponseIteration {
  const now = new Date().toISOString();
  const trimmedResponse = args.responseText.trim();
  const responseSummary = trimmedResponse.length > 180
    ? `${trimmedResponse.slice(0, 180)}...`
    : trimmedResponse;

  return {
    id: `local-response-${Date.now()}`,
    score_id: args.scoreId || "local-score",
    parent_conversation_id: null,
    user_id: "local-user",
    org_id: null,
    turn_index: args.turnIndex,
    original_prompt: args.originalPrompt,
    response_text: trimmedResponse,
    response_summary: `This response gives you material to refine, but it still needs a sharper direction before the next LLM turn.`,
    decision_translation: `The model is giving you a broad answer. The useful move now is to tell it what to narrow around so the next response becomes more specific and easier to act on.`,
    decision_point: `You are deciding whether the next response should become a decisive recommendation or stay in comparison mode long enough to expose tradeoffs.`,
    directions_json: [
      {
        direction: "Narrow into a recommendation",
        user_can_tell_it: "Narrow this into the single recommendation that matters most for the audience.",
        where_that_leads: "The next answer becomes more decisive and executive-ready.",
      },
      {
        direction: "Clarify the tradeoffs",
        user_can_tell_it: "Compare the strongest options and explain what each one costs us.",
        where_that_leads: "The next answer stays exploratory and helps you decide before committing.",
      },
    ],
    specificity_question: "What should we narrow around?",
    specificity_examples_json: [
      "the executive audience",
      "the business outcome",
      "the fastest path",
      "the lowest-risk path",
      "the most practical next step",
    ],
    user_specificity_answer: null,
    follow_up_prompt: null,
    follow_up_summary: null,
    analysis_json: {
      mock: true,
      conversation_state: "on_track",
      next_step_type: "continue_conversation",
      on_track_assessment: "The response is aligned with the original prompt, but it needs tighter direction before the next turn.",
      workflow_offer: {
        should_offer: false,
        reason: "",
        question: "",
      },
      response_excerpt: responseSummary,
    },
    created_at: now,
    updated_at: now,
  };
}

function buildMockFollowUp(iteration: PromptResponseIteration, answer: string): PromptResponseIteration {
  const now = new Date().toISOString();
  return {
    ...iteration,
    user_specificity_answer: answer,
    follow_up_summary: `This next instruction updates the prior response around: ${answer}`,
    follow_up_prompt: `Update the prior response to focus on this direction: ${answer}. Keep the useful material already drafted, remove anything that does not support this update, and return only the revised answer.`,
    analysis_json: {
      ...iteration.analysis_json,
      mock_follow_up: true,
    },
    updated_at: now,
  };
}

function FollowUpPromptWorkspace({
  iteration,
  scoreId,
  toolRecommendations,
  recommendationId,
  selectedTool,
  onIterationChange,
  onRemixPrompt,
}: {
  iteration: PromptResponseIteration;
  scoreId?: string;
  toolRecommendations?: ToolRecommendation[] | null;
  recommendationId?: string | null;
  selectedTool?: string | null;
  onIterationChange: (iteration: PromptResponseIteration) => void;
  onRemixPrompt?: (prompt: string) => void;
}) {
  const [isScoring, setIsScoring] = useState(false);
  const [optimizingTool, setOptimizingTool] = useState<string | null>(null);
  const scoringPromptRef = useRef<string | null>(null);
  const followUpPrompt = iteration.follow_up_prompt || "";
  const followUpScore = getAnalysisNumber(iteration, "follow_up_score");
  const dimensionScores = getAnalysisRecord(iteration, "follow_up_dimension_scores") as Record<string, DimensionScoreValue> | null;
  const selectedFollowUpTool = getAnalysisString(iteration, "follow_up_selected_tool") || selectedTool || null;
  const selectedFollowUpModel = getAnalysisString(iteration, "follow_up_selected_model") || getToolModel(selectedFollowUpTool, toolRecommendations);
  const cachedPrompts = useMemo(() => getFollowUpOptimizedPrompts(iteration), [iteration]);

  const persistIteration = useCallback(async (
    patch: Partial<Pick<PromptResponseIteration, "follow_up_prompt" | "follow_up_summary">>,
    analysisPatch: Record<string, unknown>
  ) => {
    const nextAnalysis = {
      ...(iteration.analysis_json || {}),
      ...analysisPatch,
    };
    const optimistic = normalizeIteration({
      ...iteration,
      ...patch,
      analysis_json: nextAnalysis,
      updated_at: new Date().toISOString(),
    });

    onIterationChange(optimistic);

    if (isMockMode || !scoreId) {
      return optimistic;
    }

    const updatePayload: Record<string, unknown> = {
      ...patch,
      analysis_json: nextAnalysis,
    };

    const { data, error } = await supabase
      .from("prompt_response_iterations")
      .update(updatePayload)
      .eq("id", iteration.id)
      .select("*")
      .single();

    if (error) {
      console.error("Failed to persist follow-up prompt state:", error);
      toast.error("Cosmo couldn't save that follow-up prompt update.");
      return optimistic;
    }

    const saved = normalizeIteration(data as PromptResponseIteration);
    onIterationChange(saved);
    return saved;
  }, [iteration, onIterationChange, scoreId]);

  const scoreFollowUpPrompt = useCallback(async (prompt: string) => {
    if (!prompt.trim()) return;
    setIsScoring(true);
    try {
      if (isMockMode) {
        await persistIteration({}, {
          follow_up_score: 83,
          follow_up_dimension_scores: {
            outcome_clarity: 13,
            context: 14,
            constraints: 12,
            specificity: 13,
            structure: 12,
            tool_fit: 10,
            iterative_guidance: 9,
          },
          follow_up_scored_prompt: prompt,
          follow_up_selected_tool: selectedFollowUpTool,
          follow_up_selected_model: selectedFollowUpModel,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("rescore-prompt", {
        body: { prompt },
      });

      if (error || !data || typeof data.score !== "number") {
        throw error || new Error("Missing score from rescore-prompt");
      }

      await persistIteration({}, {
        follow_up_score: data.score,
        follow_up_dimension_scores: data.dimension_scores || {},
        follow_up_scored_prompt: prompt,
        follow_up_selected_tool: selectedFollowUpTool,
        follow_up_selected_model: selectedFollowUpModel,
      });
    } catch (err) {
      console.error("Failed to score follow-up prompt:", err);
      toast.error("Cosmo couldn't score the follow-up prompt.");
      scoringPromptRef.current = null;
    } finally {
      setIsScoring(false);
    }
  }, [persistIteration, selectedFollowUpModel, selectedFollowUpTool]);

  useEffect(() => {
    if (!followUpPrompt.trim()) return;
    const scoredPrompt = getAnalysisString(iteration, "follow_up_scored_prompt");
    if (scoredPrompt === followUpPrompt && typeof followUpScore === "number") return;
    if (scoringPromptRef.current === followUpPrompt) return;

    scoringPromptRef.current = followUpPrompt;
    void scoreFollowUpPrompt(followUpPrompt);
  }, [followUpPrompt, followUpScore, iteration, scoreFollowUpPrompt]);

  const handlePromptUpdate = useCallback(async (newPrompt: string) => {
    const trimmedPrompt = newPrompt.trim();
    if (!trimmedPrompt || trimmedPrompt === followUpPrompt) return;
    scoringPromptRef.current = null;
    await persistIteration(
      { follow_up_prompt: newPrompt },
      {
        follow_up_score: null,
        follow_up_dimension_scores: null,
        follow_up_scored_prompt: "",
      }
    );
  }, [followUpPrompt, persistIteration]);

  const handleToolSelect = useCallback(async (toolName: string, toolModel = "") => {
    if (!followUpPrompt.trim() || optimizingTool) return;

    const cached = cachedPrompts[toolName];
    if (cached?.prompt) {
      scoringPromptRef.current = null;
      await persistIteration(
        { follow_up_prompt: cached.prompt },
        {
          follow_up_selected_tool: toolName,
          follow_up_selected_model: cached.model || toolModel,
          follow_up_score: null,
          follow_up_dimension_scores: null,
          follow_up_scored_prompt: "",
        }
      );
      return;
    }

    setOptimizingTool(toolName);
    try {
      const { data, error } = await supabase.functions.invoke("optimize-prompt-for-tool-v6", {
        body: {
          final_prompt: followUpPrompt,
          selected_tool: toolName,
          selected_model: toolModel,
          recommendation_id: recommendationId,
        },
      });

      if (error || !data?.optimized_prompt) {
        throw error || new Error("Missing optimized prompt");
      }

      const optimizedPrompt = data.optimized_prompt as string;
      scoringPromptRef.current = null;
      await persistIteration(
        { follow_up_prompt: optimizedPrompt },
        {
          follow_up_selected_tool: toolName,
          follow_up_selected_model: toolModel,
          follow_up_optimized_prompts: {
            ...cachedPrompts,
            [toolName]: {
              prompt: optimizedPrompt,
              model: toolModel,
              updated_at: new Date().toISOString(),
            },
          },
          follow_up_score: null,
          follow_up_dimension_scores: null,
          follow_up_scored_prompt: "",
        }
      );
    } catch (err) {
      console.error("Failed to optimize follow-up prompt:", err);
      toast.error("Cosmo couldn't optimize that follow-up prompt.");
    } finally {
      setOptimizingTool(null);
    }
  }, [cachedPrompts, followUpPrompt, optimizingTool, persistIteration, recommendationId]);

  return (
    <div className="mt-4 space-y-3" data-testid="follow-up-prompt-workspace">
      {typeof followUpScore === "number" ? (
        <PromptScoreSummaryBar
          flow="builder"
          improvedScore={followUpScore}
          currentScore={followUpScore}
          dimensionScores={dimensionScores || undefined}
          quickWins={[]}
        />
      ) : (
        <Card className="border-primary/20 bg-card/90 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Sparkles className="h-4 w-4 text-primary" />
              Scoring follow-up prompt
            </div>
            {isScoring && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted/70">
            <div className="h-full w-1/2 rounded-full bg-success/70" />
          </div>
        </Card>
      )}

      <ImprovedVersionCard
        title="Prompt"
        rewriteTagged={followUpPrompt}
        rewriteClean={followUpPrompt}
        rewriteSummary={iteration.follow_up_summary || undefined}
        showFavorite={false}
        contentHeightClassName="h-[260px] md:h-[320px]"
        promptPlaceholder="Edit your follow-up prompt..."
        workedLabel="It Worked"
        onPromptChange={handlePromptUpdate}
        onRemix={onRemixPrompt ? () => onRemixPrompt(followUpPrompt) : undefined}
        optimizedForTool={selectedFollowUpTool}
        modelPicker={
          <FollowUpModelPicker
            recommendations={toolRecommendations}
            selectedTool={selectedFollowUpTool}
            selectedModel={selectedFollowUpModel}
            isOptimizing={Boolean(optimizingTool)}
            disabled={!followUpPrompt.trim() || Boolean(optimizingTool)}
            onSelect={handleToolSelect}
          />
        }
      />
    </div>
  );
}

export function PromptResponsePanel({
  scoreId,
  originalPrompt,
  promptSummary,
  toolRecommendations,
  recommendationId,
  selectedTool,
  iterations,
  onIterationsChange,
  onRemixPrompt,
}: PromptResponsePanelProps) {
  const [responseText, setResponseText] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isBuildingFollowUp, setIsBuildingFollowUp] = useState<string | null>(null);
  const [specificityDrafts, setSpecificityDrafts] = useState<Record<string, string>>({});

  const latestIteration = useMemo(() => iterations[iterations.length - 1], [iterations]);

  const updateIteration = (updated: PromptResponseIteration) => {
    onIterationsChange(iterations.map((item) => (item.id === updated.id ? updated : item)));
  };

  const handleAnalyze = async () => {
    if (!scoreId || !responseText.trim() || isAnalyzing) return;
    setIsAnalyzing(true);
    try {
      if (isMockMode) {
        const iteration = buildMockAnalysis({
          scoreId,
          originalPrompt,
          responseText,
          turnIndex: iterations.length + 1,
        });
        onIterationsChange([...iterations, iteration]);
        setResponseText("");
        return;
      }

      const { data, error } = await supabase.functions.invoke("prompt-response-step", {
        body: {
          mode: "analyze",
          score_id: scoreId,
          original_prompt: originalPrompt,
          prompt_summary: promptSummary,
          response_text: responseText.trim(),
        },
      });

      if (error || data?.error) {
        throw error || new Error(data.error);
      }

      const iteration = normalizeIteration(data.iteration);
      onIterationsChange([...iterations, iteration]);
      setResponseText("");
    } catch (err) {
      console.error("Failed to analyze response:", err);
      toast.error("Cosmo couldn't analyze that response. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleBuildFollowUp = async (iteration: PromptResponseIteration) => {
    const answer = specificityDrafts[iteration.id]?.trim();
    if (!answer || isBuildingFollowUp) return;
    setIsBuildingFollowUp(iteration.id);
    try {
      if (isMockMode) {
        updateIteration(buildMockFollowUp(iteration, answer));
        setSpecificityDrafts((prev) => ({ ...prev, [iteration.id]: "" }));
        return;
      }

      const { data, error } = await supabase.functions.invoke("prompt-response-step", {
        body: {
          mode: "build_follow_up",
          iteration_id: iteration.id,
          user_specificity_answer: answer,
        },
      });

      if (error || data?.error) {
        throw error || new Error(data.error);
      }

      updateIteration(normalizeIteration(data.iteration));
      setSpecificityDrafts((prev) => ({ ...prev, [iteration.id]: "" }));
    } catch (err) {
      console.error("Failed to build follow-up prompt:", err);
      toast.error("Cosmo couldn't build the follow-up prompt. Please try again.");
    } finally {
      setIsBuildingFollowUp(null);
    }
  };

  const handleSelectSuggestion = (iterationId: string, suggestion: string) => {
    const trimmedSuggestion = suggestion.trim();
    if (!trimmedSuggestion) return;
    setSpecificityDrafts((prev) => {
      const current = prev[iterationId]?.trim();
      return {
        ...prev,
        [iterationId]: current ? `${current}\n\n${trimmedSuggestion}` : trimmedSuggestion,
      };
    });
  };

  return (
    <Card className="border-border/60 bg-card p-4 md:p-5">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10">
          <MessageSquareText className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">Response</h3>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
            Tell Cosmo what you want to understand, then paste the AI response. Cosmo will help you see what's happening and what to do next.
          </p>
          {isMockMode && (
            <p className="mt-2 text-xs text-muted-foreground">
              Local preview mode is on. This lets you test the flow without deploying backend changes.
            </p>
          )}
        </div>
      </div>

      {iterations.length > 0 && (
        <div className="mt-5 space-y-4">
          {iterations.map((iteration) => {
            const needsSpecificity = !iteration.follow_up_prompt;
            const isLatest = latestIteration?.id === iteration.id;
            const conversationState = getAnalysisString(iteration, "conversation_state");
            const conversationStateText = getConversationStateText(iteration);
            return (
              <div key={iteration.id} className="rounded-xl border border-border/60 bg-muted/15 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Response {iteration.turn_index}
                  </p>
                  {conversationState ? (
                    <span className="rounded-full border border-border/60 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      {formatStateLabel(conversationState)}
                    </span>
                  ) : iteration.response_summary ? (
                    <span className="text-xs text-muted-foreground">Analyzed</span>
                  ) : null}
                </div>

                <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary">Conversation state</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground/85">
                        {conversationStateText}
                      </p>
                    </div>
                    <div className="border-t border-border/60 pt-3 md:border-l md:border-t-0 md:pl-3 md:pt-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary">Decision point</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground/85">
                        {iteration.decision_point || "Decide how you want to steer the next AI turn."}
                      </p>
                    </div>
                  </div>
                </div>

                {iteration.directions_json.length > 0 && needsSpecificity && isLatest && (
                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-foreground">Suggested moves</p>
                      <p className="text-xs text-muted-foreground">Click one to draft the next instruction</p>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                      {iteration.directions_json.map((direction, index) => (
                        <SuggestionAction
                          key={`${iteration.id}-${index}`}
                          direction={direction}
                          onSelect={() => handleSelectSuggestion(iteration.id, direction.user_can_tell_it)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {needsSpecificity && isLatest && (
                  <div className="mt-4 rounded-lg border border-border/60 bg-background p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">Next instruction to the AI</p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {iteration.specificity_question || "Tell Cosmo what the next AI turn should change or improve."}
                        </p>
                      </div>
                      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    </div>
                    <Textarea
                      value={specificityDrafts[iteration.id] || ""}
                      onChange={(event) =>
                        setSpecificityDrafts((prev) => ({ ...prev, [iteration.id]: event.target.value }))
                      }
                      placeholder="Type your instruction, or click a suggested move above..."
                      className="mt-3 min-h-[96px] resize-y"
                    />
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => handleBuildFollowUp(iteration)}
                        disabled={!specificityDrafts[iteration.id]?.trim() || isBuildingFollowUp === iteration.id}
                        className="gap-2"
                      >
                        {isBuildingFollowUp === iteration.id ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Building...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4" />
                            Build update instruction
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {iteration.directions_json.length > 0 && (!needsSpecificity || !isLatest) && (
                  <div className="mt-4 grid gap-2 md:grid-cols-3">
                    {iteration.directions_json.map((direction, index) => (
                      <div key={`${iteration.id}-${index}`} className="rounded-lg border border-border/60 bg-background/60 p-3">
                        <p className="text-sm font-medium text-foreground">{direction.direction}</p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {direction.where_that_leads}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {iteration.follow_up_prompt && (
                  <FollowUpPromptWorkspace
                    iteration={iteration}
                    scoreId={scoreId}
                    toolRecommendations={toolRecommendations}
                    recommendationId={recommendationId}
                    selectedTool={selectedTool}
                    onIterationChange={updateIteration}
                    onRemixPrompt={onRemixPrompt}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-5">
        <Textarea
          value={responseText}
          onChange={(event) => setResponseText(event.target.value)}
          placeholder={"Ask Cosmo what you want to understand, then paste the AI response here...\n\nExample: Is this still on track?\n\n[Paste the AI response]"}
          className="min-h-[150px] resize-y"
          disabled={!scoreId || isAnalyzing}
        />
        <div className="mt-3 flex justify-end">
          <Button onClick={handleAnalyze} disabled={!scoreId || !responseText.trim() || isAnalyzing} className="gap-2">
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <SendHorizontal className="h-4 w-4" />
                Analyze response
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
